import React, { useState } from 'react';
import styles from './Modal.module.css';
import { PlanningItem, PlanningType, PlanningStatus, WerkCategorie } from './types';

interface AddItemModalProps {
  onClose: () => void;
  onSave: (item: Omit<PlanningItem, 'id'>) => void;
  editItem?: PlanningItem;
}

export const AddItemModal: React.FC<AddItemModalProps> = ({ onClose, onSave, editItem }) => {
  const [formData, setFormData] = useState<Omit<PlanningItem, 'id'>>({
    type: editItem?.type || 'communicatie',
    title: editItem?.title || '',
    date: editItem?.date || '',
    time: editItem?.time || '09:00',
    targetAudience: editItem?.targetAudience || '',
    channel: editItem?.channel || '',
    responsible: editItem?.responsible || '',
    status: editItem?.status || 'Gepland',
    location: editItem?.location || '',
    dependencies: editItem?.dependencies || [],
    description: editItem?.description || '',
    werkCategorie: editItem?.werkCategorie || 'project',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field: keyof Omit<PlanningItem, 'id'>, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{editItem ? 'Planning item bewerken' : 'Nieuw planning item'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalContent}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="type">Type</label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value as PlanningType)}
                required
              >
                <option value="mijlpaal">Projectmijlpaal</option>
                <option value="communicatie">Communicatiemoment</option>
                <option value="bijeenkomst">Omgevingsbijeenkomst</option>
                <option value="deadline">Deadline</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value as PlanningStatus)}
                required
              >
                <option value="Gepland">Gepland</option>
                <option value="In voorbereiding">In voorbereiding</option>
                <option value="Bevestigd">Bevestigd</option>
                <option value="Afgerond">Afgerond</option>
                <option value="Geannuleerd">Geannuleerd</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="werkCategorie">Werkzaamheden categorie</label>
            <select
              id="werkCategorie"
              value={formData.werkCategorie || 'project'}
              onChange={(e) => handleChange('werkCategorie', e.target.value as WerkCategorie)}
            >
              <option value="project">Projectwerkzaamheden (eigen project)</option>
              <option value="raakvlak">Raakvlakproject (extern/derden)</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="title">Titel</label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Bijv. Bewonersbrief versturen"
              required
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="date">Datum</label>
              <input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="time">Tijd</label>
              <input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) => handleChange('time', e.target.value)}
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="targetAudience">Doelgroep</label>
            <input
              id="targetAudience"
              type="text"
              value={formData.targetAudience}
              onChange={(e) => handleChange('targetAudience', e.target.value)}
              placeholder="Bijv. Direct omwonenden"
              required
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="channel">Kanaal/Middel</label>
              <select
                id="channel"
                value={formData.channel}
                onChange={(e) => handleChange('channel', e.target.value)}
              >
                <option value="">Selecteer...</option>
                <option value="Brief">Brief</option>
                <option value="Email">Email</option>
                <option value="Website">Website</option>
                <option value="Social Media">Social Media</option>
                <option value="Fysieke bijeenkomst">Fysieke bijeenkomst</option>
                <option value="Online meeting">Online meeting</option>
                <option value="Nieuwsbrief">Nieuwsbrief</option>
                <option value="Persbericht">Persbericht</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="responsible">Verantwoordelijke</label>
              <input
                id="responsible"
                type="text"
                value={formData.responsible}
                onChange={(e) => handleChange('responsible', e.target.value)}
                placeholder="Naam of functie"
                required
              />
            </div>
          </div>

          {formData.type === 'bijeenkomst' && (
            <div className={styles.formGroup}>
              <label htmlFor="location">Locatie</label>
              <input
                id="location"
                type="text"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Bijv. Gemeentehuis"
              />
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="description">Beschrijving (optioneel)</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Extra informatie of notities..."
              rows={3}
            />
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnSecondary} onClick={onClose}>
              Annuleren
            </button>
            <button type="submit" className={styles.btnPrimary}>
              {editItem ? 'Opslaan' : 'Toevoegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};